import React, { useRef, useEffect, useState } from "react";
import Button from "../../../../components/Button/Button";
import VerbacallIcon from "../../../../assets/svg/verbacall-icon";
import VerbamailIcon from "../../../../assets/svg/verbamail-icon";
import AddUserIcon from "../../../../assets/svg/add-user-icon";
import WorkflowIcon from "../../../../assets/svg/workflow-icon";
import RightArrowIcon from "../../../../assets/svg/right-arrow-icon";
import AddIcon from "../../../../assets/svg/add-icon";
import { QuickActionsPopupProps } from "./type";
import DynamicLeadCreateEditForm from "../../../../pages/Communications/components/CommunicationDetailsPanel/components/DynamicLeadCreateEditForm";
import { ExtendedLeadFormValues } from "../../../../pages/Communications/components/CommunicationDetailsPanel/utils/types";
import {
  useCreateLeadMutation,
  useCreateVfLeadMutation,
  useGetLeadPropertiesListQuery,
} from "../../../../redux/services/crmApi";
import { useGetAllAgentsQuery } from "../../../../redux/services/userManagementApi";
import AddViewingDrawer from "../../../../pages/Crm/components/LeadDetails/components/LeftSubPanel/components/AddViewingDrawer";
import ViewingQADrawer from "../../../../pages/Crm/components/LeadDetails/components/LeftSubPanel/components/ViewingQADrawer";
import { customToast } from "../../../../utils/custom-toast/custom-toast";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setDrawer } from "../../../../redux/slices/userManagementSlice";
import { CreateUserDrawer } from "../../../../pages/UserManagement/components/CreateEditUserDrawer/CreateUserDrawer/CreateUserDrawer";
import DialerModal from "../../../DialerModal/DialerModal";
import { useTwilioCallHandler } from "../../../../hooks/use-twilio-call-handler";
import { getClientDomain } from "../../../../utils/functions/client-code";
import { openComposeEmailModal } from "../../../../redux/slices/composeEmailModalSlice";
import { resetEmailEditor } from "../../../../redux/slices/emailEditorSlice";
import {
  getDefaultBudgetDuration,
  getDefaultCurrencyUnit,
  getLeaseDurationUnit,
  getRegionLabel,
} from "../../../../utils/constants/mappings";
import { getRegionCallingCode } from "../../../../utils/constants/regionDefaultCallingCodes";
import { prependLeadToStage } from "../../../../redux/slices/crmLeadListSlice";
import { useGetIntegrationsPresentQuery } from "../../../../redux/services/communicationApi";
import { trackEvents } from "../../../../utils/amplitude/amplitude";
import VfCreateLeadForm, { VfLeadFormValues } from "./VfCreateLeadForm";
import {
  setDrawer as setCrmDrawer,
  clearDrawer as clearCrmDrawer,
} from "../../../../redux/slices/drawerSlice";

const QuickActionsPopup: React.FC<QuickActionsPopupProps> = ({
  isOpen,
  onClose,
  handleOpenAddContentDrawer,
}) => {
  const client = useSelector((state: RootState) => state.auth.client);
  const isVfClient = client?.client_code === "verbaflo";
  const createLeadLabel = "Create a Lead";
  const orgId = client?.client_code || "unknown";
  const userId =
    useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const drawer = useSelector((state: RootState) => state.userManagement.drawer);
  const leadDetails = useSelector(
    (state: RootState) => state.leadDetails.selectedLeadDetails
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const menuRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  const crmDrawer = useSelector((state: RootState) => state.drawer);
  const isCreateLeadDrawerOpen =
    crmDrawer.open && crmDrawer.type === "create_lead";
  const isCreateViewingDrawerOpen =
    crmDrawer.open && crmDrawer.type === "create_viewing";
  const isViewingQADrawerOpen =
    crmDrawer.open && crmDrawer.type === "viewing_qa" && Boolean(crmDrawer.id);

  const [viewingType, setViewingType] = useState<"on-site" | "virtual">(
    "on-site"
  );

  const [createLead, { isLoading: isCreatingLead }] = useCreateLeadMutation();
  const [createVfLead, { isLoading: isCreatingVfLead }] =
    useCreateVfLeadMutation();
  const { data: integrationsPresent } = useGetIntegrationsPresentQuery({
    skip: !isOpen,
  });

  useGetLeadPropertiesListQuery(
    { managerId: client?.client_code || "", leadId: undefined },
    { skip: !isCreateViewingDrawerOpen }
  );
  useGetAllAgentsQuery("", { skip: !isCreateViewingDrawerOpen });

  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const [dialerNumber, setDialerNumber] = useState("");
  const [countryCode, setCountryCode] = useState(
    client?.region
      ? getRegionCallingCode(client?.region)
      : leadDetails?.country_code
        ? leadDetails?.country_code
        : "+44"
  );

  const { handleMakeCall } = useTwilioCallHandler(
    "",
    "anonymous",
    undefined,
    countryCode && dialerNumber ? `${countryCode}${dialerNumber}` : undefined
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "Tab":
          if (
            !e.shiftKey &&
            e.target === document.activeElement?.lastElementChild
          ) {
            e.preventDefault();
            firstButtonRef.current?.focus();
          }
          break;
        case "ArrowDown": {
          e.preventDefault();
          const buttons = menuRef.current?.querySelectorAll("button");
          const currentIndex = Array.from(buttons || []).findIndex(
            (button) => button === document.activeElement
          );
          const nextIndex = (currentIndex + 1) % (buttons?.length || 1);
          buttons?.[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const buttons = menuRef.current?.querySelectorAll("button");
          const currentIndex = Array.from(buttons || []).findIndex(
            (button) => button === document.activeElement
          );
          const prevIndex =
            currentIndex <= 0 ? (buttons?.length || 1) - 1 : currentIndex - 1;
          buttons?.[prevIndex]?.focus();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleOpenCreateLeadDrawer = () => {
    dispatch(setCrmDrawer({ open: true, type: "create_lead", id: "enquiry" }));
    onClose();
  };

  const handleCloseCreateLeadDrawer = () => {
    dispatch(clearCrmDrawer());
  };

  const handleCloseCreateViewingDrawer = () => {
    dispatch(clearCrmDrawer());
  };

  const handleCreateUserClick = () => {
    dispatch(setDrawer({ open: true, type: "create_user" }));
    onClose();
  };

  const parseRangeString = (
    range: string
  ): { min: number; max: number } | null => {
    if (!range) return null;
    if (range.includes("+")) {
      const min = parseInt(range.replace("+", ""));
      return isNaN(min) ? null : { min, max: min };
    }
    const parts = range.split("-");
    if (parts.length === 2) {
      const min = parseInt(parts[0]);
      const max = parseInt(parts[1]);
      return isNaN(min) || isNaN(max) ? null : { min, max };
    }
    const num = parseInt(range, 10);
    if (!isNaN(num)) return { min: num, max: num };
    return null;
  };

  const handleVfCreateLeadSubmit = async (values: VfLeadFormValues) => {
    const payload: Record<string, unknown> = {
      name: values.name,
      email: values.email,
      phone: values.phoneNumber || undefined,
      phone_country_code: values.phoneCountry?.dialCode || undefined,
      company_name: values.company_name,
      industry: values.industry,
      job_title: values.job_title || undefined,
      comment: values.how_can_verbaflo_help || undefined,
      number_of_beds: parseRangeString(values.number_of_beds_managed),
      number_of_properties: parseRangeString(values.number_of_properties),
      demographics: {
        ...(values.country_name && {
          destination_country: values.country_name,
          destination_country_id: values.country
            ? parseInt(values.country)
            : null,
        }),
        ...(values.city_names?.[0] && {
          destination_city: values.city_names[0],
          destination_city_id: values.city?.[0]
            ? parseInt(values.city[0])
            : null,
        }),
        ...(values.timezone && {
          timezone: values.timezone,
        }),
      },
      office_location: values.office_location || undefined,
      external_crm_name: values.crm_currently_used || undefined,
      external_pms_name: values.pms_currently_used || undefined,
      external_voip_name: values.voip || undefined,
      demo_1_date: values.demo_1_date || null,
      demo_2_date: values.demo_2_date || null,
      demo_3_date: values.demo_3_date || null,
      demo_4_date: values.demo_4_date || null,
      verbaflo_request_priority: values.priority || undefined,
      contact_person_name: values.contact_person_name || undefined,
      contact_person_email: values.contact_person_email || undefined,
      contact_person_designation:
        values.contact_person_designation || undefined,
      website_url: values.website || undefined,
      contract_link: values.contract_link || null,
      fathom_link: values.fathom_link || null,
      agent_id: values.agent_id.length > 0 ? values.agent_id : null,
    };

    Object.keys(payload).forEach(
      (key) =>
        payload[key as keyof typeof payload] === undefined &&
        delete payload[key as keyof typeof payload]
    );

    try {
      const res = await createVfLead(payload).unwrap();
      if (res.current_stage) {
        dispatch(prependLeadToStage({ stageId: res.current_stage, lead: res }));
      }
      trackEvents("lead_created", {
        source: "vf_form",
        created_by: userId,
        org_id: orgId,
      });
      customToast(
        "Lead created successfully",
        "success",
        undefined,
        undefined,
        "bottom-left"
      );
      handleCloseCreateLeadDrawer();
    } catch (error) {
      trackEvents("lead_creation_failed", {
        org_id: orgId,
        error: error instanceof Error ? error.message : String(error),
      });
      customToast(
        "Failed to create lead",
        "error",
        undefined,
        undefined,
        "bottom-left"
      );
    }
  };

  const handleCreateLeadSubmit = async (values: ExtendedLeadFormValues) => {
    const payload: Record<string, unknown> = {
      name: values.name,
      email: values.email || undefined,
      room_type: values.roomType.length > 0 ? values.roomType : undefined,
      level_of_study: values.levelOfStudy || undefined,
      tenant_type: values.tenantType || undefined,
      move_in_date: values.move_in_date
        ? values.move_in_date.toISOString().split("T")[0]
        : undefined,
    };

    if (values.phoneNumber && values.phoneCountry?.dialCode) {
      payload.phone_country_code = values.phoneCountry?.dialCode;
      payload.phone = values.phoneNumber;
    }
    if (values.whatsappNumber && values.whatsappCountry?.dialCode) {
      payload.whatsapp_phone_country_code = values.whatsappCountry?.dialCode;
      payload.whatsapp_phone = values.whatsappNumber;
    }

    if (
      values.destinationCountry ||
      values.destinationCity ||
      values.university
    ) {
      payload.demographics = {
        ...(values.destinationCountry && {
          destination_country_id: parseInt(values.destinationCountry),
          destination_country: values.destinationCountryName,
        }),
        ...(values.destinationCity && {
          destination_city_id: parseInt(values.destinationCity),
          destination_city: values.destinationCityName,
        }),
        ...(values.university && {
          university_id: parseInt(values.university),
          university: values.universityName,
        }),
      };
    }

    if (values.min_budget || values.max_budget) {
      payload.budget = {
        ...(values.min_budget && { min_budget: parseFloat(values.min_budget) }),
        ...(values.max_budget && { max_budget: parseFloat(values.max_budget) }),
        ...(getDefaultCurrencyUnit(client?.region) && {
          currency: getDefaultCurrencyUnit(client?.region),
        }),
        ...(getDefaultBudgetDuration(client?.region) && {
          duration: getDefaultBudgetDuration(client?.region),
        }),
      };
    }

    if (values.stay_duration) {
      payload.lease = {
        value: parseInt(values.stay_duration),
        unit: getLeaseDurationUnit(client?.region) || "",
      };
    }

    if (values?.utmSource || values?.utmMedium) {
      payload.tracking_info = {
        ...(values?.utmSource && { utm_source: values?.utmSource }),
        ...(values?.utmMedium && { utm_medium: values?.utmMedium }),
      };
    }

    if (
      values?.choosenPropsPayload &&
      values?.choosenPropsPayload?.length > 0
    ) {
      payload.choosen_props = values.choosenPropsPayload;
    }

    if (values.selectedTagsDataWithColors) {
      payload.tags = values.selectedTagsDataWithColors;
    }

    if (values.currentStage) {
      payload.current_stage = values.currentStage;
    }

    if (values.moveInSession) {
      payload.academic_year = values.moveInSession;
    }

    if (values.howDidYouHearAboutUs) {
      payload.how_did_you_hear_about_us = values.howDidYouHearAboutUs;
    }

    if (values.marketingConsent) {
      payload.marketing_consent = values.marketingConsent === "Yes";
    }

    if (values.assigned_agents && values.assigned_agents.length > 0) {
      payload.agent_id = values.assigned_agents;
    }

    if (crmDrawer.id) {
      payload.view_id = crmDrawer.id;
    }

    Object.keys(payload).forEach(
      (key) =>
        payload[key as keyof typeof payload] === undefined &&
        delete payload[key as keyof typeof payload]
    );

    try {
      const res = await createLead(payload).unwrap();
      if (res.current_stage) {
        dispatch(prependLeadToStage({ stageId: res.current_stage, lead: res }));
      }
      trackEvents("lead_created", {
        source: values.utmSource || "manual",
        assigned_to: res.agent?.name || res.agent?.id || "unassigned",
        priority: res.lead_score || "unknown",
        created_by: userId,
        org_id: orgId,
      });
      customToast(
        "Lead created successfully",
        "success",
        undefined,
        undefined,
        "bottom-left"
      );
      handleCloseCreateLeadDrawer();
    } catch (error) {
      trackEvents("lead_creation_failed", {
        org_id: orgId,
        error: error instanceof Error ? error.message : String(error),
      });
      customToast(
        "Failed to create lead",
        "error",
        undefined,
        undefined,
        "bottom-left"
      );
    }
  };

  if (
    !isOpen &&
    !isCreateLeadDrawerOpen &&
    !isCreateViewingDrawerOpen &&
    !isViewingQADrawerOpen &&
    !drawer?.open &&
    !isDialerOpen
  )
    return null;

  const handleOpenCreateWorkFlo = () => {
    navigate(`/dashboard/campaigns/create?step=category`);
    onClose();
  };

  const handleOpenDialer = () => {
    if (getClientDomain() === "ukdemosuite") {
      setCountryCode("+44");
      setDialerNumber("7426931503");
    }
    setIsDialerOpen(true);
    onClose();
  };

  const handleEmailBuilderClick = () => {
    dispatch(resetEmailEditor());
    dispatch(
      openComposeEmailModal({
        title: "New Email",
        viewState: "mini",
        leadId: "",
        leadEmail: "",
        isFromQuickActions: true,
      })
    );
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          ref={menuRef}
          className="flex flex-col gap-[6px] px-[8px] py-[8px] w-fit min-w-[180px] absolute top-10 z-99999 right-0 bg-popper-bg/80 dark:bg-popper-bg-dk/80 backdrop-blur-[20px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-profile-card"
          role="menu"
          aria-label="Quick actions"
          data-quick-actions-popup
        >
          <>
            {integrationsPresent?.outbound_integrations?.call && (
              <Button
                ref={firstButtonRef}
                ariaLabel="Make a Call"
                innerText="Make a Call"
                Icon={VerbacallIcon}
                SecondaryIcon={RightArrowIcon}
                iconLeft={true}
                iconClass="w-[18px] h-[18px] text-verba-lavender dark:text-verba-lavender-dk shrink-0"
                innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap"
                secondaryButtonContainerClass="w-full"
                secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
                buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
                role="menuitem"
                onClick={handleOpenDialer}
              />
            )}
            {integrationsPresent?.outbound_integrations?.email && (
              <Button
                ariaLabel="Send an Email"
                innerText="Send an Email"
                Icon={VerbamailIcon}
                SecondaryIcon={RightArrowIcon}
                secondaryButtonContainerClass="w-full"
                secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
                iconLeft={true}
                iconClass="w-[18px] h-[18px] text-verba-mail dark:text-verba-mail-dk shrink-0"
                innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap"
                buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
                role="menuitem"
                onClick={handleEmailBuilderClick}
              />
            )}
            {/* <Button
                ariaLabel="Send WhatsApp"
                innerText="Send WhatsApp"
                Icon={VerbamessengerIcon}
                SecondaryIcon={RightArrowIcon}
                secondaryButtonContainerClass="w-full"
                secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk"
                iconLeft={true}
                iconClass="w-[18px] h-[18px] text-verba-messenger dark:text-verba-messenger-dk"
                innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
                buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
                role="menuitem"
              /> */}
            {integrationsPresent?.outbound_integrations?.call &&
              integrationsPresent?.outbound_integrations?.email && (
                <hr className="border-border-gray dark:border-border-gray-dk" />
              )}
            <Button
              ariaLabel="Create a User"
              innerText="Create a User"
              Icon={AddUserIcon}
              SecondaryIcon={RightArrowIcon}
              secondaryButtonContainerClass="w-full"
              secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
              iconLeft={true}
              iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk group-focus:text-verba-blue dark:group-focus:text-verba-blue-dk shrink-0"
              innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap"
              buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
              role="menuitem"
              onClick={handleCreateUserClick}
            />
            <Button
              ariaLabel="Create WorkFlo"
              innerText="Create WorkFlo"
              Icon={WorkflowIcon}
              SecondaryIcon={RightArrowIcon}
              secondaryButtonContainerClass="w-full"
              secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
              iconLeft={true}
              iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk group-focus:text-verba-blue dark:group-focus:text-verba-blue-dk shrink-0"
              innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap"
              buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
              role="menuitem"
              onClick={handleOpenCreateWorkFlo}
            />
            <hr className="border-border-gray dark:border-border-gray-dk" />
            <Button
              ariaLabel={createLeadLabel}
              innerText={createLeadLabel}
              Icon={AddIcon}
              SecondaryIcon={RightArrowIcon}
              secondaryButtonContainerClass="w-full"
              secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
              iconLeft={true}
              iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk group-focus:text-verba-blue dark:group-focus:text-verba-blue-dk shrink-0"
              innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap"
              buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
              role="menuitem"
              onClick={handleOpenCreateLeadDrawer}
            />
            <hr className="border-border-gray dark:border-border-gray-dk" />
            <Button
              ariaLabel="Add Content"
              innerText={
                <p className="flex flex-col justify-start">
                  <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                    Add Content
                  </span>
                  <span className="text-gray-placeholder dark:text-gray-placeholder-dk text-xxs font-normal whitespace-nowrap">
                    {getRegionLabel(
                      "knowledgeBaseSection",
                      client?.client_code
                    )}
                  </span>
                </p>
              }
              Icon={AddIcon}
              SecondaryIcon={RightArrowIcon}
              secondaryButtonContainerClass="w-full"
              secondaryIconClass="w-[18px] h-[18px] text-transparent dark:text-transparent group-hover:text-verba-indigo dark:group-hover:text-verba-indigo-dk group-focus:text-verba-indigo dark:group-focus:text-verba-indigo-dk shrink-0"
              iconLeft={true}
              iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk group-focus:text-verba-blue dark:group-focus:text-verba-blue-dk shrink-0"
              innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
              buttonContainerClass="rounded-[8px] w-full justify-start px-[8px] py-[9px] group bg-transparent dark:bg-transparent hover:bg-hover-effect hover:dark:bg-hover-effect-dk focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk"
              role="menuitem"
              onClick={handleOpenAddContentDrawer}
            />
          </>
        </div>
      )}

      {isVfClient ? (
        <VfCreateLeadForm
          openDrawer={isCreateLeadDrawerOpen}
          handleCloseDrawer={handleCloseCreateLeadDrawer}
          onSubmit={handleVfCreateLeadSubmit}
          isUpdating={isCreatingVfLead}
        />
      ) : (
        <DynamicLeadCreateEditForm
          openDrawer={isCreateLeadDrawerOpen}
          handleCloseDrawer={handleCloseCreateLeadDrawer}
          size="380px"
          isEdit={false}
          isDowning={getClientDomain() === "downing"}
          onSubmit={handleCreateLeadSubmit}
          validations={["name", "email"]}
          isUpdating={isCreatingLead}
          viewId={crmDrawer?.id}
        />
      )}

      {isCreateViewingDrawerOpen && (
        <AddViewingDrawer
          open={isCreateViewingDrawerOpen}
          onClose={handleCloseCreateViewingDrawer}
          viewingType={viewingType}
          setViewingType={setViewingType}
          leadId=""
          leadName=""
          leadEmail=""
          showLeadId
        />
      )}

      {isViewingQADrawerOpen && crmDrawer?.id && (
        <ViewingQADrawer viewingId={crmDrawer?.id} />
      )}

      {drawer?.open && drawer?.type === "create_user" && <CreateUserDrawer />}

      <DialerModal
        isOpen={isDialerOpen}
        onOpenChange={setIsDialerOpen}
        dialerNumber={dialerNumber}
        setDialerNumber={setDialerNumber}
        setCountryCode={setCountryCode}
        handleMakeCall={handleMakeCall}
        initialCountryCode={countryCode}
        className="fixed! top-1/2! left-1/2! -translate-x-1/2! -translate-y-1/2! z-99999!"
      />
    </>
  );
};

export default QuickActionsPopup;
